import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import LogoMark from './ui/LogoMark';
import { COLORS } from '../constants/theme';
import { useAuth } from '../context/AuthContext';

const links = [
  { label: 'Home',     href: '#home' },
  { label: 'About',    href: '#about' },
  { label: 'Features', href: '#features' },
  { label: 'Products', href: '#products' },
  { label: 'Contact',  href: '#contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen]         = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const onHero = !scrolled;
  const { user, logout } = useAuth();

  return (
    <motion.nav
      initial={{ y: -72, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        background: scrolled ? 'rgba(255,255,255,0.94)' : 'transparent',
        backdropFilter: scrolled ? 'blur(20px)' : 'none',
        borderBottom: scrolled ? `1px solid ${COLORS.border}` : '1px solid transparent',
        boxShadow: scrolled ? '0 1px 16px rgba(15,35,53,0.07)' : 'none',
        transition: 'background .3s, border-color .3s, box-shadow .3s, backdrop-filter .3s',
      }}
    >
      <div className="container" style={{ display: 'flex', alignItems: 'center', height: 70 }}>

        {/* Logo */}
        <a href="#home" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none', marginRight: 'auto' }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10,
            background: onHero ? 'rgba(255,255,255,0.2)' : `linear-gradient(135deg,${COLORS.blue},${COLORS.blueDark})`,
            border: onHero ? '1.5px solid rgba(255,255,255,0.4)' : 'none',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            transition: 'background .3s, border .3s',
          }}>
            <LogoMark />
          </div>
          <span style={{ fontSize: 17, fontWeight: 700, letterSpacing: '-.025em', color: onHero ? COLORS.white : COLORS.text, transition: 'color .3s' }}>
            Reano{' '}
            <span style={{ color: onHero ? 'rgba(255,255,255,0.85)' : COLORS.blue, transition: 'color .3s' }}>AI</span>
          </span>
        </a>

        {/* Desktop nav */}
        <ul style={{ display: 'flex', gap: 2, listStyle: 'none', padding: 0, margin: '0 24px 0 0' }} className="nav-desktop">
          {links.map(l => (
            <li key={l.href}>
              <a href={l.href} className={`nav-link ${onHero ? 'nav-link--hero' : 'nav-link--scrolled'}`}>{l.label}</a>
            </li>
          ))}
        </ul>

        {/* Pulse button — always visible */}
        <Link to="/pulse" className="nav-pulse-btn"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 7,
            padding: '8px 16px', borderRadius: 9, marginRight: 16,
            background: '#0f172a',
            border: '1.5px solid rgba(34,211,238,0.45)',
            color: '#fff', fontSize: 13.5, fontWeight: 700,
            textDecoration: 'none', letterSpacing: '-.01em',
            boxShadow: '0 0 14px rgba(34,211,238,0.2)',
            transition: 'box-shadow .2s, transform .15s, border-color .2s',
          }}
          onMouseEnter={e => { e.currentTarget.style.boxShadow='0 0 24px rgba(34,211,238,0.4)'; e.currentTarget.style.borderColor='rgba(34,211,238,0.75)'; e.currentTarget.style.transform='translateY(-1px)'; }}
          onMouseLeave={e => { e.currentTarget.style.boxShadow='0 0 14px rgba(34,211,238,0.2)'; e.currentTarget.style.borderColor='rgba(34,211,238,0.45)'; e.currentTarget.style.transform='none'; }}
        >
          <svg width="14" height="14" viewBox="0 0 32 32" fill="none">
            <polyline points="4,16 8,16 10,9 13,23 16,12 19,20 22,16 28,16"
              stroke="#22D3EE" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Try Pulse
        </Link>

        {/* Divider */}
        <div className="nav-desktop" style={{ width: 1, height: 22, background: onHero ? 'rgba(255,255,255,0.25)' : COLORS.border, marginRight: 16, flexShrink: 0 }} />

        {/* CTA buttons */}
        <div className="nav-desktop" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {user ? (
            <>
              <Link to="/dashboard" style={{
                display: 'inline-flex', alignItems: 'center', gap: 7,
                fontSize: 13.5, fontWeight: 700, padding: '8px 16px', borderRadius: 9,
                background: '#0f172a',
                border: '1.5px solid rgba(34,211,238,0.45)',
                color: '#fff', textDecoration: 'none', letterSpacing: '-.01em',
                boxShadow: '0 0 14px rgba(34,211,238,0.2)',
                transition: 'box-shadow .2s, transform .15s, border-color .2s',
              }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow='0 0 24px rgba(34,211,238,0.4)'; e.currentTarget.style.borderColor='rgba(34,211,238,0.75)'; e.currentTarget.style.transform='translateY(-1px)'; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow='0 0 14px rgba(34,211,238,0.2)'; e.currentTarget.style.borderColor='rgba(34,211,238,0.45)'; e.currentTarget.style.transform='none'; }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#22D3EE" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
                </svg>
                Dashboard
              </Link>
              <button onClick={() => { logout(); }} style={{
                fontSize: 14, fontWeight: 600, padding: '9px 20px', borderRadius: 9,
                background: 'transparent', cursor: 'pointer',
                border: onHero ? '1.5px solid rgba(255,255,255,0.4)' : `1.5px solid ${COLORS.border}`,
                color: onHero ? COLORS.white : COLORS.text2,
                transition: 'border-color .2s, color .2s',
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = onHero ? 'rgba(255,255,255,0.7)' : COLORS.blue; e.currentTarget.style.color = onHero ? '#fff' : COLORS.blue; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = onHero ? 'rgba(255,255,255,0.4)' : COLORS.border; e.currentTarget.style.color = onHero ? COLORS.white : COLORS.text2; }}
              >Logout</button>
            </>
          ) : (
            <Link to="/auth" style={{
              fontSize: 14, fontWeight: 600, padding: '9px 20px', borderRadius: 9,
              background: onHero ? 'rgba(255,255,255,0.18)' : COLORS.blue,
              border: onHero ? '1.5px solid rgba(255,255,255,0.4)' : '1.5px solid transparent',
              color: COLORS.white, textDecoration: 'none',
              transition: 'background .2s',
            }}
              onMouseEnter={e => { e.currentTarget.style.background = onHero ? 'rgba(255,255,255,0.28)' : COLORS.blueDark; }}
              onMouseLeave={e => { e.currentTarget.style.background = onHero ? 'rgba(255,255,255,0.18)' : COLORS.blue; }}
            >Sign In</Link>
          )}
        </div>

        {/* Hamburger */}
        <button onClick={() => setOpen(o => !o)} className="nav-hamburger"
          style={{ display: 'none', background: 'none', border: 'none', cursor: 'pointer', color: onHero ? COLORS.white : COLORS.text, padding: 6 }}
          aria-label="Menu">
          <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.8">
            {open
              ? <><line x1="4" y1="4" x2="18" y2="18"/><line x1="18" y1="4" x2="4" y2="18"/></>
              : <><line x1="3" y1="7" x2="19" y2="7"/><line x1="3" y1="11" x2="19" y2="11"/><line x1="3" y1="15" x2="19" y2="15"/></>}
          </svg>
        </button>
      </div>

      {/* Mobile drawer */}
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22 }}
            style={{ overflow: 'hidden', background: COLORS.white, borderTop: `1px solid ${COLORS.border}`, boxShadow: '0 8px 24px rgba(15,35,53,0.08)' }}>
            <div className="container" style={{ padding: '14px 28px 20px' }}>
              {links.map(l => (
                <a key={l.href} href={l.href} onClick={() => setOpen(false)} style={{
                  display: 'block', padding: '11px 0',
                  fontSize: 15, fontWeight: 500, color: COLORS.text2, textDecoration: 'none',
                  borderBottom: `1px solid ${COLORS.bg}`,
                }}>{l.label}</a>
              ))}
              <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {user ? (
                  <>
                    <Link to="/dashboard" onClick={() => setOpen(false)} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                      padding: '12px 0', borderRadius: 10,
                      background: '#0f172a', border: '1.5px solid rgba(34,211,238,0.45)',
                      color: '#fff', fontSize: 15, fontWeight: 700,
                      textDecoration: 'none', boxShadow: '0 0 16px rgba(34,211,238,0.2)',
                    }}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#22D3EE" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
                      </svg>
                      Dashboard
                    </Link>
                    <button onClick={() => { logout(); setOpen(false); }} style={{
                      padding: '12px 0', borderRadius: 10, cursor: 'pointer',
                      background: 'transparent', border: `1.5px solid ${COLORS.border}`,
                      color: COLORS.text2, fontSize: 15, fontWeight: 600,
                    }}>Logout</button>
                  </>
                ) : (
                  <Link to="/auth" className="btn-primary" onClick={() => setOpen(false)} style={{ justifyContent: 'center', fontSize: 15 }}>Sign In</Link>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        .nav-link {
          display: block; padding: 6px 14px; border-radius: 8px;
          font-size: 14px; font-weight: 500;
          text-decoration: none; transition: color .15s, background .15s;
        }
        .nav-link--hero         { color: rgba(255,255,255,0.82); }
        .nav-link--hero:hover   { color: #fff; background: rgba(255,255,255,0.12); }
        .nav-link--scrolled     { color: #4A6070; }
        .nav-link--scrolled:hover { color: #0F2335; background: #F0F4F8; }
        @media (max-width: 800px) {
          .nav-desktop        { display: none !important; }
          .nav-hamburger      { display: flex !important; }
          .nav-pulse-btn      { padding: 7px 12px !important; font-size: 12.5px !important; margin-right: 10px !important; }
          .nav-pulse-btn span { display: none; }
        }
      `}</style>
    </motion.nav>
  );
}
